import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { aboutData, faqData } from '@/mocks/articles';

export default function AboutPage() {
  const { t } = useTranslation();

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '吃瓜网',
    url: 'https://chiguawang.com',
    description: '吃瓜网官网独家爆料，24小时速报黑料八卦，吃瓜第一站',
    email: aboutData.contact.email,
    address: {
      '@type': 'PostalAddress',
      addressLocality: '北京',
      addressRegion: '朝阳区',
      streetAddress: '建国路88号SOHO现代城A座',
    },
    sameAs: [
      'https://weibo.com/chiguawang',
      'https://weixin.qq.com/chiguawang',
    ],
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: t('breadcrumbHome'),
        item: 'https://chiguawang.com',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: t('aboutBreadcrumb'),
        item: 'https://chiguawang.com/about',
      },
    ],
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '关于吃瓜网',
    description: '了解吃瓜网品牌故事、核心团队和联系方式',
    url: 'https://chiguawang.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '吃瓜网',
      url: 'https://chiguawang.com',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <Navbar />
      <main>
        <section className="bg-background-50 pt-24 md:pt-28 pb-8">
          <div className="px-4 md:px-6 max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: t('breadcrumbHome'), to: '/' },
                { label: t('aboutBreadcrumb') },
              ]}
            />

            <div className="text-center mt-10 mb-14">
              <span className="inline-block px-4 py-1.5 text-xs font-medium text-primary-700 border border-primary-300 rounded-full">
                {t('aboutHeroTag')}
              </span>
              <h1 className="mt-6 text-3xl md:text-5xl font-heading font-bold text-foreground-950 leading-tight">
                {t('aboutHeroTitle1')}
              </h1>
              <p className="mt-2 text-2xl md:text-4xl font-heading font-bold text-foreground-950">
                {t('aboutHeroTitle2')}
              </p>
              <p className="mt-4 text-xl md:text-3xl font-heading font-bold text-accent-600">
                {t('aboutHeroTitle3')}
              </p>
            </div>
          </div>
        </section>

        <section className="bg-background-100 py-14 md:py-20">
          <div className="px-4 md:px-6 max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-6">
              {t('aboutBrandTitle')}
            </h2>
            <p className="text-base text-foreground-700 leading-relaxed">
              {aboutData.brandIntro}
            </p>
          </div>
        </section>

        <section className="bg-background-50 py-14 md:py-20">
          <div className="px-4 md:px-6 max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-6">
              {t('aboutMissionTitle')}
            </h2>
            <p className="text-base text-foreground-700 leading-relaxed">
              {aboutData.mission}
            </p>
          </div>
        </section>

        <section className="bg-background-100 py-14 md:py-20">
          <div className="px-4 md:px-6 max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-6">
              {t('aboutTeamTitle')}
            </h2>
            <p className="text-base text-foreground-700 leading-relaxed">
              {aboutData.teamIntro}
            </p>
          </div>
        </section>

        <section className="bg-background-50 py-14 md:py-20">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-12">
              {t('aboutCredibilityTitle')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {aboutData.credibility.map((item, idx) => (
                <div
                  key={idx}
                  className={`rounded-xl p-8 text-center ${
                    idx === 0
                      ? 'bg-foreground-950 text-background-50'
                      : idx === 1
                      ? 'bg-accent-500 text-background-50'
                      : 'bg-background-100 border border-background-200/70'
                  }`}
                >
                  <span className={`text-3xl md:text-4xl font-heading font-bold block ${
                    idx === 2 ? 'text-foreground-950' : 'text-background-50'
                  }`}>
                    {item.label}
                  </span>
                  <p className={`mt-3 text-lg font-semibold ${
                    idx === 2 ? 'text-foreground-900' : 'text-background-50/90'
                  }`}>
                    {item.value}
                  </p>
                  <p className={`mt-3 text-sm leading-relaxed ${
                    idx === 2 ? 'text-foreground-500' : 'text-background-200/80'
                  }`}>
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-background-100 py-14 md:py-20">
          <div className="px-4 md:px-6 max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-12">
              {t('aboutValuesTitle')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {aboutData.values.map((val, idx) => (
                <div key={idx} className="text-center">
                  <div className="w-14 h-14 mx-auto rounded-full bg-accent-100 flex items-center justify-center">
                    <span className="text-accent-600 font-heading font-bold text-xl">
                      {idx + 1}
                    </span>
                  </div>
                  <h3 className="mt-5 text-lg font-heading font-bold text-foreground-950">
                    {val.title}
                  </h3>
                  <p className="mt-3 text-sm text-foreground-500 leading-relaxed">
                    {val.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-foreground-950 py-14 md:py-20">
          <div className="px-4 md:px-6 max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50 text-center mb-12">
              {t('aboutContactTitle')}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: 'ri-mail-line', label: t('aboutEmailLabel'), value: aboutData.contact.email },
                { icon: 'ri-wechat-line', label: t('aboutWechatLabel'), value: aboutData.contact.wechat },
                { icon: 'ri-weibo-line', label: t('aboutWeiboLabel'), value: aboutData.contact.weibo },
                { icon: 'ri-map-pin-line', label: t('aboutAddressLabel'), value: aboutData.contact.address },
              ].map((item, idx) => (
                <div key={idx} className="bg-foreground-900 rounded-xl p-6 text-center">
                  <div className="w-10 h-10 mx-auto rounded-full bg-foreground-800 flex items-center justify-center">
                    <i className={`${item.icon} text-accent-400 text-lg`} />
                  </div>
                  <p className="mt-3 text-xs text-foreground-400">{item.label}</p>
                  <p className="mt-1 text-sm text-background-100 font-medium">{item.value}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-background-50 py-14 md:py-20">
          <div className="px-4 md:px-6 max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-12">
              {t('aboutFaqTitle')}
            </h2>
            <div className="space-y-4">
              {faqData.map((faq, idx) => (
                <details key={idx} className="group bg-background-100 rounded-xl border border-background-200/70">
                  <summary className="px-6 py-5 cursor-pointer flex items-center justify-between gap-4">
                    <span className="text-base font-medium text-foreground-900">{faq.question}</span>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform text-lg flex-shrink-0" />
                  </summary>
                  <div className="px-6 pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
      </main>
      <Footer />
    </>
  );
}